import React, { useState, useRef, useEffect } from "react";
import useTimer from "./useTimer";
import { useSelector } from "react-redux";
import customerSelector from "../../redux/selectors/customer.selector";
import OtpTimer from "./OtpTimer";

const CheckCircleFilled = ({ size = 64, className = "" }) => (
  // Filled style for success (simulated Ant Design CheckCircleFilled)
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
  </svg>
);

const RedoOutlined = ({ size = 16, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M21.5 2v6h-6" />
    <path d="M2.5 12a10 10 0 0 1 17.5-6l-2.1-2.1" />
  </svg>
);

const OTPForm = ({
  countryCode,
  mobileNumber,
  onVerify,
  onChangeNumber,
  onSendOtp,
  isSending,
  isOtpBeingVerified,
}) => {
  const [otp, setOtp] = useState(["", "", "", "", ""]); // 5 digits
  // const [isVerifying, setIsVerifying] = useState(false);
  const inputRefs = useRef([]);
  const otpErrorMessage = useSelector(customerSelector.otpErrorMessage);
  // Focus management logic
  const handleOtpChange = (index, value) => {
    // Only allow single digit input
    const newDigit = value.slice(-1);
    if (!/^\d*$/.test(newDigit)) return;

    const newOtp = [...otp];
    newOtp[index] = newDigit;
    setOtp(newOtp);

    // Auto-focus logic
    if (newDigit && index < 4) {
      inputRefs.current[index + 1].focus();
    }
  };

  // Backspace/navigation logic
  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleVerify = () => {
    const fullOtp = otp.join("");
    if (fullOtp.length !== 5 || isOtpBeingVerified) return;

    // setIsVerifying(true);
    onVerify(fullOtp);
  };
  const isOtpComplete = otp.every((digit) => digit.length === 1);
  const buttonDisabled = !isOtpComplete || isOtpBeingVerified;

  const handleResend = () => {
    onSendOtp(mobileNumber, countryCode);
  };

  return (
    <div className="form-section">
      <h2 className="title-medium">Verify OTP</h2>
      <p className="subtitle-small">
        A 5-digit code has been sent to{" "}
        <span className="highlight-text">
          +{countryCode + mobileNumber}
        </span>
      </p>
      <p className="link-wrapper">
        <OtpTimer 
          handleResend={handleResend}
          isSending={isSending}
        />
      </p>
      {/* OTP Digits Input */}
      <div className="otp-container">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="tel"
            maxLength="1"
            value={digit}
            onChange={(e) => handleOtpChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            className="otp-input"
            // Ensure input is cleared on focus for better touch UX
            onFocus={(e) => e.target.select()}
          />
        ))}
        {otpErrorMessage && <div className="error-message">{otpErrorMessage}</div>}
      </div>

      {/* Verify Button */}
      <button
        onClick={handleVerify}
        disabled={buttonDisabled}
        className={`btn btn-lg btn-verify ${buttonDisabled ? "disabled" : ""}`}
      >
        {isOtpBeingVerified ? (
          <div className="spinner"></div>
        ) : (
          // Using Ant Design name: CheckCircleFilled
          <>
            <CheckCircleFilled size={20} className="icon-margin" /> Verify Code
          </>
        )}
      </button>

      {/* Back to Mobile Number link */}
      <p className="link-wrapper">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            onChangeNumber();
          }}
          className="action-link"
        >
          Change Mobile Number
        </a>
      </p>
    </div>
  );
};

export default OTPForm;
